import { createAsyncThunk } from "@reduxjs/toolkit";

import logger from "Libs/logger";
import client, { entities, request } from "Libs/platform";
import { hasHtml, sleep } from "Libs/utils";
import { selectUserProjectById } from "Reducers/app/selectors";
import { selectFeatureOrganizationsEnabled } from "Reducers/featureFlags/featureFlags.selectors";
import {
  organizationsSelector,
  organizationSelector,
  getOrganization
} from "Reducers/organization/organization";
import {
  loadOneProjectHelper,
  subscribe,
  LoadOneProjectType
} from "Reducers/project/project";

import type { Project } from "@packages/client";
import type { AsyncThunkOptionType } from "Reducers/types";

const RETRY_ATTEMPTS = 3;
const RETRY_DELAY_MS = 5000;
const RETRYABLE_STATUS_CODES = [502, 503];

/**
 * Rejection value produced when `Project.get` resolves to nothing after its
 * retries — the regional cluster hasn't surfaced the project yet. Consumers
 * match on this exact string to detect the post-creation 404 race, so it lives
 * here as the single source of truth alongside the thunk that produces it.
 */
export const PROJECT_NOT_FOUND = "Project not found";

/**
 * Pulls an HTTP-style status code out of a thunk rejection payload, normalizing
 * across the shapes `loadOneProject` can produce: `{ code }`, `{ status }`, and
 * a bare string containing a 3-digit code (e.g. "502 Bad Gateway"). Returns
 * undefined for payloads with no recognizable status — including the explicit
 * `PROJECT_NOT_FOUND` rejection; treat that as a domain-level signal at the
 * call site, not as a generic 404.
 *
 * When a payload carries both `code` and `status`, `code` wins. This is the
 * right value for the display and 404-detection call sites; retry membership
 * is handled separately by `isRetryableError`, which inspects both fields.
 */
export const extractStatusCode = (payload: unknown): number | undefined => {
  if (typeof payload === "string") {
    const match = payload.match(/\b(\d{3})\b/);
    return match ? Number(match[1]) : undefined;
  }
  if (!payload || typeof payload !== "object") return undefined;
  const obj = payload as { code?: number; status?: number };
  return obj.code ?? obj.status;
};

const isRetryableError = (err: unknown): boolean => {
  if (typeof err === "string") {
    const code = extractStatusCode(err);
    return code !== undefined && RETRYABLE_STATUS_CODES.includes(code);
  }
  // Check `code` and `status` independently: a payload carrying both (e.g.
  // `{ code: 404, status: 502 }`) must still retry on the retryable field
  // rather than collapsing to the single value `extractStatusCode` returns.
  const obj = (err ?? {}) as { code?: number; status?: number };
  return RETRYABLE_STATUS_CODES.some(
    code => code === obj.code || code === obj.status
  );
};

export const loadOneProject = createAsyncThunk<
  undefined,
  LoadOneProjectType,
  AsyncThunkOptionType
>(
  "app/project_reducer/load_one_project",
  async (
    { projectId, organizationId, loadActivitiesAndSubscribe = true },
    { dispatch, getState, rejectWithValue }
  ) => {
    const organizationEnabled = selectFeatureOrganizationsEnabled(getState());

    let projectMe: Project | undefined = undefined;

    try {
      if (!organizationEnabled) {
        // Get the project id from the project description id in the
        // Project loaded from /me
        projectMe = selectUserProjectById(getState(), {
          projectId
        });

        if (!projectMe) {
          projectMe = (await request(
            `${client.getConfig().api_url}/locate/${projectId}`,
            "GET"
          )) as Project;
        }
      }

      let project: Project | undefined;
      let attempt = 0;

      while (!project && attempt <= RETRY_ATTEMPTS) {
        try {
          project = await entities.Project.get({ id: projectId });
        } catch (err) {
          if (!isRetryableError(err) || attempt >= RETRY_ATTEMPTS) {
            throw err;
          }
          await sleep(RETRY_DELAY_MS);
        }
        attempt++;
      }

      if (!project) {
        return rejectWithValue(PROJECT_NOT_FOUND);
      }

      if (organizationEnabled) {
        let organization = organizationSelector(getState(), {
          organizationId: project.organization_id
        });

        if (!organization) {
          // Load the organization
          await dispatch(
            getOrganization({
              organizationDescriptionId: project.organization_id
            })
          );
        }
      }

      const organizations = organizationsSelector(getState());

      dispatch(
        loadOneProjectHelper({
          project,
          projectMe,
          organizations,
          // Only a fallback for the store key when the organization can't be
          // resolved. Callers with organizations enabled sometimes pass the
          // organization id instead of its name, which would key the project
          // under the id and redirect the URL to it.
          organizationId: organizationEnabled ? undefined : organizationId
        })
      );

      if (loadActivitiesAndSubscribe) {
        dispatch(subscribe({ project, organizationId }));
      }
    } catch (err) {
      // Only read the status from error objects: plain-text error bodies can
      // mention 400/403/404 while the response itself was a 5xx.
      const status =
        typeof err === "object" ? extractStatusCode(err) : undefined;
      if (
        !(status !== undefined && [404, 403, 400].includes(status)) &&
        !hasHtml(err as string)
      ) {
        logger(err, {
          action: "project_load",
          projectId
        });
      }
      return rejectWithValue(err);
    }
  }
);
